/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

/**
 * Utility pro operace s Promise.
 */
class PromiseUtil {
  /**
   * Funguje stejně jako standardní `Promise.all`, ale předávané pole je
   * ve stejném formátu jako {@link PromiseUtil.allSerial}, tj. pole továren
   * na Promise.
   *
   * Slouží zejména pro rychlé přepnutí allSerial varianty na paralelní.
   *
   * Nedostatky a nepodporované funkcionality:
   * - Vstup by měl být Iterable, ale podporováno je zatím jen Array.
   * - Vyžaduje se, aby funkce vracely Promise.
   * - Pravděpodobně i další, nebylo implementováno do detailu dle specifikace.
   *
   * Zdroje:
   * - [MDN Promise.all](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise/all)
   * @template {readonly (() => Promise<unknown>)[] | []} T
   * @param {T} array
   * @returns {Promise<{ -readonly [P in keyof T]: Awaited<ReturnType<T[P]>> }>}
   */
  static allParallel(array) {
    return /** @type {Promise<{ -readonly [P in keyof T]: Awaited<ReturnType<T[P]>> }>} */ (
      Promise.all(array.map((fn) => fn()))
    );
  }

  /**
   * Funguje podobně jako standardní `Promise.all`, akorát se jednotlivé Promise
   * volají sériově a nikoliv paralelně.
   *
   * Nedostatky a nepodporované funkcionality:
   * - Vstup by měl být Iterable, ale podporováno je zatím jen Array.
   * - Vyžaduje se, aby funkce vracely Promise.
   * - Pravděpodobně i další, nebylo implementováno do detailu dle specifikace.
   *
   * Zdroje:
   * - [MDN Promise.all](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise/all)
   * @template {readonly (() => Promise<unknown>)[] | []} T
   * @param {T} array
   * @returns {Promise<{ -readonly [P in keyof T]: Awaited<ReturnType<T[P]>> }>}
   */
  static allSerial(array) {
    return new Promise((resolve) =>
      resolve(
        array.reduce(
          (acc, fn, i) =>
            acc.then((results) =>
              fn().then((result) => {
                results[i] = result;
                return results;
              })
            ),
          /** @type {Promise<{ -readonly [P in keyof T]: Awaited<ReturnType<T[P]>> }>} */
          (Promise.resolve(Array(array.length)))
        )
      )
    );
  }

  /**
   * @template T
   * @param {() => Promise<T>} fn
   * @returns {Promise<{ result: T; durationMs: number }>}
   */
  static async measureTime(fn) {
    const start = Date.now();
    const result = await fn();
    const end = Date.now();
    return { result, durationMs: end - start };
  }

  /**
   * @template T
   * @param {() => Promise<T>} operation
   * @param {Object} options
   * @param {number} options.max Maximální počet pokusů
   * @param {number} options.initialDelayMs Počáteční zpoždění v ms
   * @param {(attempt: number, delay: number) => number} [options.nextDelay] Funkce pro výpočet zpoždění před dalším pokusem,
   * která bere aktuální pokus a zpoždění jako argumenty a vrací nové zpoždění. Výchozí je konstantní zpoždění.
   * @returns {Promise<T>}
   */
  static async retry(operation, options) {
    const {
      max,
      initialDelayMs,
      nextDelay = (attempt, delay) => delay
    } = options;

    if (!Number.isFinite(max) || !Number.isInteger(max) || max < 0) {
      throw new TypeError(
        'PromiseUtil.retry: "max" must be a finite non-negative integer'
      );
    }

    if (!Number.isFinite(initialDelayMs) || initialDelayMs < 0) {
      throw new TypeError(
        'PromiseUtil.retry: "initialDelayMs" must be a finite non-negative integer'
      );
    }

    let attempts = 0;
    let delay = initialDelayMs;

    while (true) {
      try {
        // eslint-disable-next-line no-await-in-loop
        return await operation();
      } catch (error) {
        attempts += 1;
        if (attempts >= max) {
          throw error;
        }
        // eslint-disable-next-line no-await-in-loop
        await PromiseUtil.wait(delay);
        delay = nextDelay(attempts, delay);
      }
    }
  }

  /**
   * // TODO: přidat možnost zrušení čekání pomocí AbortControlleru
   * Počká dokud neuplyne pořadovaný čas.
   * @param {number} [delayMs] Čas po který se čeká, než se Promise vyřeší. [ms]
   * @returns {Promise<void>}
   */
  static wait(delayMs) {
    return new Promise((resolve) => setTimeout(resolve, delayMs));
  }
}

export default PromiseUtil;
